/* Excision STEP viewer. Loaded only on product pages with a STEP metafield. */
(function () {
  'use strict';

  if (window.ExcisionStepViewer) {
    window.ExcisionStepViewer.init(document);
    return;
  }

  const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.174.0/build/three.module.js';
  const OCCT_BASE = 'https://cdn.jsdelivr.net/npm/occt-import-js@0.0.23/dist/';
  const modelStates = new WeakMap();
  let threePromise;

  function getThree() {
    if (!threePromise) {
      threePromise = import(THREE_URL).catch(error => {
        threePromise = null;
        throw error;
      });
    }
    return threePromise;
  }

  function convertInWorker(fileBuffer) {
    return new Promise((resolve, reject) => {
      // STEP triangulation is CPU-intensive. Keep it off the storefront's main thread.
      const workerCode = `
        self.onmessage = async function (event) {
          try {
            importScripts(${JSON.stringify(OCCT_BASE + 'occt-import-js.js')});
            const occt = await self.occtimportjs({
              locateFile: function (name) { return ${JSON.stringify(OCCT_BASE)} + name; }
            });
            const result = occt.ReadStepFile(new Uint8Array(event.data), {
              linearUnit: 'millimeter',
              linearDeflectionType: 'bounding_box_ratio',
              linearDeflection: 0.002,
              angularDeflection: 0.4
            });
            self.postMessage(result);
          } catch (error) {
            self.postMessage({ success: false, error: String(error && error.message || error) });
          }
        };
      `;
      let worker;
      let blobUrl;
      try {
        blobUrl = URL.createObjectURL(new Blob([workerCode], { type: 'text/javascript' }));
        worker = new Worker(blobUrl);
      } catch (error) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        reject(error);
        return;
      }
      const cleanup = () => {
        worker.terminate();
        URL.revokeObjectURL(blobUrl);
      };
      worker.addEventListener('message', event => {
        cleanup();
        const result = event.data;
        if (!result || result.success !== true || !Array.isArray(result.meshes)) {
          reject(new Error(result && result.error || 'The STEP file could not be converted.'));
          return;
        }
        resolve(result);
      }, { once: true });
      worker.addEventListener('error', event => {
        cleanup();
        reject(new Error(event.message || 'The STEP converter could not start.'));
      }, { once: true });
      worker.postMessage(fileBuffer, [fileBuffer]);
    });
  }

  // Read the live Excision palette. Fallbacks match the supplied design system.
  function getExcisionPalette() {
    const css = getComputedStyle(document.documentElement);
    const read = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
    return {
      primaryBlue: read('--color-primary-blue', '#0368B2'),
      secondaryBlue: read('--color-secondary-blue', '#369BF3'),
      primaryOrange: read('--color-primary-orange', '#E85A22'),
      secondaryOrange: read('--color-secondary-orange', '#ff9933'),
      tertiaryGreen: read('--color-tertiary-green', '#0DC36C'),
      tertiaryRed: read('--color-tertiary-red', '#ea2b2e'),
      primaryGrey: read('--color-primary-grey', '#f1f1f1'),
      secondaryGrey: read('--color-secondary-grey', '#9ca3af'),
      secondaryIron: read('--color-secondary-iron', '#4b5563'),
      surface: read('--color-surface', '#ffffff')
    };
  }
  function displayModel(state, THREE, imported) {
    const { canvasHost, resetButton, panel } = state;
    if (!state.root.isConnected) return;

    const palette = getExcisionPalette();
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(palette.primaryGrey);
    const camera = new THREE.PerspectiveCamera(36, 1, 0.01, 100000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    canvasHost.appendChild(renderer.domElement);
    renderer.domElement.setAttribute('aria-label', 'Interactive 3D product model. Drag to rotate, scroll or pinch to zoom.');
    renderer.domElement.setAttribute('role', 'img');
    renderer.domElement.style.touchAction = 'none';

    const group = new THREE.Group();
    const modelParts = [];
    let triangleCount = 0;
    for (const item of imported.meshes) {
      const data = item && item.attributes;
      if (!data || !data.position || !data.position.array || !data.position.array.length) continue;
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(data.position.array, 3));
      if (item.index && item.index.array && item.index.array.length) {
        geometry.setIndex(item.index.array);
      }
      if (data.normal && data.normal.array && data.normal.array.length === data.position.array.length) {
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(data.normal.array, 3));
      } else {
        geometry.computeVertexNormals();
      }
      const partTriangles = geometry.index ? geometry.index.count / 3 : geometry.attributes.position.count / 3;
      triangleCount += partTriangles;
      // STEP appearance is read-only. Keep each body's original CAD colour available.
      const originalColor = Array.isArray(item.color) && item.color.length >= 3
        ? new THREE.Color().setRGB(item.color[0], item.color[1], item.color[2], THREE.SRGBColorSpace)
        : new THREE.Color(palette.secondaryGrey);
      const material = new THREE.MeshStandardMaterial({
        color: originalColor.clone(), metalness: 0.12, roughness: 0.63, side: THREE.DoubleSide
      });
      const mesh = new THREE.Mesh(geometry, material);
      group.add(mesh);
      modelParts.push({ material, originalColor, triangles: partTriangles, edgeMaterial: null });
    }
    if (!group.children.length) {
      renderer.dispose();
      renderer.domElement.remove();
      throw new Error('The STEP file did not contain any renderable geometry.');
    }

    // Crisp CAD edges on modest models, omitted on very complex STEP meshes.
    if (triangleCount < 180000) {
      group.children.forEach((mesh, index) => {
        const edges = new THREE.EdgesGeometry(mesh.geometry, 28);
        const edgeMaterial = new THREE.LineBasicMaterial({
          color: palette.secondaryIron, transparent: true, opacity: 0.43
        });
        mesh.add(new THREE.LineSegments(edges, edgeMaterial));
        modelParts[index].edgeMaterial = edgeMaterial;
      });
    }

    // Remap CAD colour families to Excision palette tokens. Only the preview
    // changes. The original STEP file is always downloaded unmodified.
    function classifyCadColour(originalColor) {
      const hsl = { h: 0, s: 0, l: 0 };
      originalColor.getHSL(hsl, THREE.SRGBColorSpace);
      const rgb = originalColor.getRGB({ r: 0, g: 0, b: 0 }, THREE.SRGBColorSpace);
      const hue = hsl.h * 360;
      const saturation = hsl.s;
      const lightness = hsl.l;
      const chroma = Math.max(rgb.r, rgb.g, rgb.b) - Math.min(rgb.r, rgb.g, rgb.b);
      const hasBlueHue = hue >= 175 && hue < 261;

      // Hue alone is NOT enough: slightly blue-tinted machine greys are still
      // greys. A genuine blue must be visibly saturated AND chromatic.
      const neutral = saturation < 0.30 || chroma < 0.17 ||
        (hasBlueHue && (saturation < 0.55 || chroma < 0.30));
      if (neutral) {
        // Keep black / white as authored; map other neutral shades to the
        // three actual Excision greys, retaining their relative brightness.
        if (lightness < 0.10 || lightness > 0.97) return null;
        // Reserve iron for genuinely dark CAD surfaces. The majority of
        // neutral machined parts should render in medium or light grey.
        if (lightness < 0.28) return 'secondaryIron';
        if (lightness < 0.73) return 'secondaryGrey';
        return 'primaryGrey';
      }

      // Yellow-coated cutting ends use Excision's lighter orange.
      if (hue >= 42 && hue < 73) return 'secondaryOrange';
      if (hue >= 16 && hue < 42) {
        return lightness > 0.56 ? 'secondaryOrange' : 'primaryOrange';
      }
      if (hue < 16 || hue >= 344) return 'tertiaryRed';
      if (hue >= 73 && hue < 175) return 'tertiaryGreen';
      if (hue >= 175 && hue < 261) {
        return lightness > 0.53 ? 'secondaryBlue' : 'primaryBlue';
      }

      // Purple and other colours without a matching Excision family are kept.
      return null;
    }

    modelParts.forEach(part => {
      const family = classifyCadColour(part.originalColor);
      if (!family) return;

      part.material.color.set(palette[family]);
      const neutral = family === 'secondaryIron' || family === 'primaryGrey' || family === 'secondaryGrey';
      // Lower metallic reflectivity keeps light/medium greys from
      // appearing unnecessarily dark under the scene lighting.
      part.material.metalness = neutral ? 0.12 : 0.18;
      part.material.roughness = neutral ? 0.64 : 0.52;
      if (part.edgeMaterial) {
        part.edgeMaterial.color.set(palette.secondaryIron);
        part.edgeMaterial.opacity = neutral
          ? (family === 'secondaryIron' ? 0.22 : 0.14)
          : 0.32;
      }
      part.material.needsUpdate = true;
    });

    const bounds = new THREE.Box3().setFromObject(group);
    const centre = bounds.getCenter(new THREE.Vector3());
    const dimensions = bounds.getSize(new THREE.Vector3());
    const scale = Math.max(dimensions.x, dimensions.y, dimensions.z);
    if (!Number.isFinite(scale) || scale <= 0) throw new Error('The STEP file has invalid dimensions.');
    group.position.sub(centre);
    scene.add(group);

    // A feathered contact shadow gives the product a suspended-in-air look.
    // One 128px texture and two triangles; no shadow-map pass or animation.
    const shadowCanvas = document.createElement('canvas');
    shadowCanvas.width = 128;
    shadowCanvas.height = 128;
    const shadowContext = shadowCanvas.getContext('2d');
    let shadowTexture = null;
    let floatingShadow = null;
    if (shadowContext) {
      const falloff = shadowContext.createRadialGradient(64, 64, 0, 64, 64, 64);
      falloff.addColorStop(0, 'rgba(0, 0, 0, 0.28)');
      falloff.addColorStop(0.35, 'rgba(0, 0, 0, 0.17)');
      falloff.addColorStop(0.70, 'rgba(0, 0, 0, 0.045)');
      falloff.addColorStop(1, 'rgba(0, 0, 0, 0)');
      shadowContext.fillStyle = falloff;
      shadowContext.fillRect(0, 0, 128, 128);
      shadowTexture = new THREE.CanvasTexture(shadowCanvas);
      floatingShadow = new THREE.Mesh(
        new THREE.PlaneGeometry(
          Math.max(dimensions.x, scale * 0.3) * 1.65,
          Math.max(dimensions.z, scale * 0.3) * 1.65
        ),
        new THREE.MeshBasicMaterial({
          map: shadowTexture,
          transparent: true,
          depthWrite: false,
          side: THREE.DoubleSide,
          toneMapped: false
        })
      );
      floatingShadow.rotation.x = -Math.PI / 2;
      floatingShadow.position.set(0, -dimensions.y / 2 - scale * 0.14, 0);
      floatingShadow.renderOrder = -1;
      scene.add(floatingShadow);
    }

    scene.add(new THREE.HemisphereLight(0xffffff, new THREE.Color(palette.secondaryGrey), 2.3));
    const topLight = new THREE.DirectionalLight(0xffffff, 2.3);
    topLight.position.set(1, 2, 3);
    scene.add(topLight);
    const sideLight = new THREE.DirectionalLight(0xffffff, 0.9);
    sideLight.position.set(-2, 1, -1);
    scene.add(sideLight);

    const orbit = { yaw: 0.65, pitch: 1.12, radius: scale * 2.35, target: new THREE.Vector3() };
    const defaultOrbit = { ...orbit, target: new THREE.Vector3() };
    const pointers = new Map();
    let lastPinch = null;

    function draw() {
      if (panel.hidden || !canvasHost.isConnected) return;
      const width = canvasHost.clientWidth;
      const height = canvasHost.clientHeight;
      if (!width || !height) return;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.near = Math.max(0.001, scale / 1000);
      camera.far = Math.max(scale * 1000, orbit.radius * 20);
      camera.updateProjectionMatrix();
      camera.position.set(
        orbit.target.x + orbit.radius * Math.sin(orbit.pitch) * Math.sin(orbit.yaw),
        orbit.target.y + orbit.radius * Math.cos(orbit.pitch),
        orbit.target.z + orbit.radius * Math.sin(orbit.pitch) * Math.cos(orbit.yaw)
      );
      camera.lookAt(orbit.target);
      if (floatingShadow) floatingShadow.visible = orbit.pitch < Math.PI / 2;
      renderer.render(scene, camera);
    }

    function zoomBy(factor) {
      orbit.radius = Math.max(scale * 0.15, Math.min(scale * 45, orbit.radius * factor));
      draw();
    }

    renderer.domElement.addEventListener('pointerdown', event => {
      renderer.domElement.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    });
    renderer.domElement.addEventListener('pointermove', event => {
      const previous = pointers.get(event.pointerId);
      if (!previous) return;
      const dx = event.clientX - previous.x;
      const dy = event.clientY - previous.y;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const two = [...pointers.values()];
        const pinch = Math.hypot(two[0].x - two[1].x, two[0].y - two[1].y);
        if (lastPinch !== null && lastPinch > 0) zoomBy(lastPinch / Math.max(1, pinch));
        lastPinch = pinch;
      } else if (event.buttons === 2 || event.shiftKey) {
        const amount = orbit.radius / Math.max(240, canvasHost.clientHeight);
        camera.updateMatrix();
        const right = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 0);
        const up = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 1);
        orbit.target.addScaledVector(right, -dx * amount);
        orbit.target.addScaledVector(up, dy * amount);
        draw();
      } else {
        orbit.yaw -= dx * 0.008;
        orbit.pitch = Math.min(Math.PI - 0.05, Math.max(0.05, orbit.pitch - dy * 0.008));
        draw();
      }
    });
    function stopPointer(event) {
      pointers.delete(event.pointerId);
      lastPinch = null;
    }
    renderer.domElement.addEventListener('pointerup', stopPointer);
    renderer.domElement.addEventListener('pointercancel', stopPointer);
    renderer.domElement.addEventListener('lostpointercapture', stopPointer);
    renderer.domElement.addEventListener('contextmenu', event => event.preventDefault());
    renderer.domElement.addEventListener('wheel', event => {
      event.preventDefault();
      zoomBy(Math.exp(Math.sign(event.deltaY) * 0.12));
    }, { passive: false });

    resetButton.hidden = false;
    resetButton.addEventListener('click', () => {
      orbit.yaw = defaultOrbit.yaw;
      orbit.pitch = defaultOrbit.pitch;
      orbit.radius = defaultOrbit.radius;
      orbit.target.copy(defaultOrbit.target);
      draw();
    });
    const resizeObserver = new ResizeObserver(draw);
    resizeObserver.observe(canvasHost);
    state.draw = draw;
    state.dispose = () => {
      resizeObserver.disconnect();
      scene.traverse(object => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach(material => material.dispose());
        }
      });
      if (shadowTexture) shadowTexture.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
    draw();
  }

  async function begin(state) {
    if (state.loading || state.loaded || !state.root.isConnected) {
      if (state.loaded && state.draw) state.draw();
      return;
    }
    state.loading = true;
    state.status.hidden = false;
    state.status.textContent = 'Loading 3D model…';
    try {
      const url = new URL(state.panel.dataset.stepUrl, document.baseURI);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid STEP file URL.');
      const [THREE, response] = await Promise.all([
        getThree(),
        fetch(url.href, { credentials: 'omit' })
      ]);
      if (!response.ok) throw new Error('STEP file request failed (' + response.status + ').');
      const fileBuffer = await response.arrayBuffer();
      if (!fileBuffer.byteLength) throw new Error('The STEP file is empty.');
      state.status.textContent = 'Preparing 3D geometry…';
      // Transfer the file to the worker. This avoids copying a large CAD file.
      const result = await convertInWorker(fileBuffer);
      displayModel(state, THREE, result);
      state.loaded = true;
      state.status.hidden = true;
    } catch (error) {
      console.error('[Excision STEP viewer]', error);
      state.status.hidden = false;
      state.status.textContent = '3D preview unavailable. You can still download the STEP file.';
    } finally {
      state.loading = false;
    }
  }

  function init(root) {
    const gallery = root.closest('[id^="ProductGallery-"]');
    if (!gallery || modelStates.has(root)) return;
    const canvasHost = root.querySelector('[data-step-canvas]');
    const status = root.querySelector('[data-step-status]');
    const resetButton = root.querySelector('[data-step-reset]');
    if (!canvasHost || !status || !resetButton) return;
    const state = {
      root: gallery, panel: root, canvasHost, status, resetButton,
      loaded: false, loading: false
    };
    modelStates.set(root, state);
    gallery.addEventListener('ex:step:open', () => begin(state));
    if (!root.hidden) begin(state);
    // Render once again when the gallery becomes visible after image switching.
    gallery.addEventListener('ex:step:open', () => { if (state.draw) requestAnimationFrame(state.draw); });
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(gallery) && state.dispose) state.dispose();
    });
  }

  function initAll(container) {
    container.querySelectorAll('[data-step-panel]').forEach(init);
  }
  window.ExcisionStepViewer = { init: initAll };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', event => initAll(event.target));
}());